import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, Database, Loader2, Play, TrendingUp, AlertTriangle } from 'lucide-react';
import { executeCompleteMigration } from '@/scripts/executeMigration';

interface MigrationResult {
  total: number;
  successful: number;
  failed: number;
  errors: string[];
}

interface FinalStats {
  totalCourses: number;
  totalModules: number;
  coursesByCategory: Record<string, number>;
}

const MigrationExecutor: React.FC = () => {
  const [isExecuting, setIsExecuting] = useState(false);
  const [migrationComplete, setMigrationComplete] = useState(false);
  const [migrationResult, setMigrationResult] = useState<MigrationResult | null>(null);
  const [finalStats, setFinalStats] = useState<FinalStats | null>(null);
  const [executionLog, setExecutionLog] = useState<string[]>([]);

  const handleExecuteMigration = async () => {
    setIsExecuting(true);
    setExecutionLog(['🚀 Starting complete course migration...']);
    
    try {
      const result = await executeCompleteMigration();
      
      setMigrationResult(result.migrationResults);
      setFinalStats(result.finalStats);
      setMigrationComplete(true);
      
      setExecutionLog(prev => [
        ...prev,
        `✅ Migration completed: ${result.migrationResults.successful}/${result.migrationResults.total} courses`,
        `📚 Total courses in database: ${result.finalStats.totalCourses}`,
        `📖 Total modules: ${result.finalStats.totalModules}`,
        `🎯 Categories populated: ${Object.keys(result.finalStats.coursesByCategory).length}`
      ]);
      
    } catch (error) {
      console.error('Migration execution failed:', error);
      setExecutionLog(prev => [
        ...prev,
        `❌ Migration failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      ]);
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Complete Course Migration Executor
        </CardTitle>
        <CardDescription>
          Execute the complete migration of all legacy courses to the database
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        
        {!migrationComplete && !isExecuting && (
          <Alert className="border-blue-200 bg-blue-50">
            <Play className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Ready for Migration:</strong> This will migrate 100+ courses across all categories 
              from legacy files to the database. The process includes course metadata, modules, and category assignments.
            </AlertDescription>
          </Alert>
        )}

        {!migrationComplete && (
          <Button 
            onClick={handleExecuteMigration}
            disabled={isExecuting}
            className="w-full"
            size="lg"
          >
            {isExecuting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Executing Migration...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Execute Complete Migration
              </>
            )}
          </Button>
        )}

        {/* Execution Log */}
        {executionLog.length > 0 && (
          <Card className="bg-gray-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Execution Log</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1 max-h-40 overflow-y-auto text-xs font-mono">
                {executionLog.map((log, index) => (
                  <div key={index} className="text-gray-700">{log}</div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Migration Results */}
        {migrationComplete && migrationResult && finalStats && (
          <div className="space-y-4">
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Migration Executed Successfully!</strong> All legacy courses have been processed 
                and migrated to the database.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-primary/10 rounded-lg">
                <div className="text-2xl font-bold text-primary">{migrationResult.total}</div>
                <div className="text-sm text-muted-foreground">Total Processed</div>
              </div>
              <div className="text-center p-4 bg-green-100 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{migrationResult.successful}</div>
                <div className="text-sm text-muted-foreground">Successfully Migrated</div>
              </div>
              <div className="text-center p-4 bg-blue-100 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{finalStats.totalCourses}</div>
                <div className="text-sm text-muted-foreground">Total in Database</div>
              </div>
              <div className="text-center p-4 bg-purple-100 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{finalStats.totalModules}</div>
                <div className="text-sm text-muted-foreground">Total Modules</div>
              </div>
            </div>

            {migrationResult.failed > 0 && (
              <Alert className="border-orange-200 bg-orange-50">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800">
                  <strong>{migrationResult.failed} courses failed to migrate.</strong> 
                  Check the execution log for details.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Final Database Statistics
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {Object.entries(finalStats.coursesByCategory)
                  .sort(([,a], [,b]) => b - a)
                  .map(([category, count]) => (
                    <div key={category} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <span className="text-xs font-medium capitalize">
                        {category.replace('-', ' ')}
                      </span>
                      <Badge variant="secondary" className="text-xs">{count}</Badge>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MigrationExecutor;